import { apiSlice } from './apiSlice';
import { decodeToken, encodeToken } from '../../utils/TokenUtils';

const buildCreateCenterFormData = (data: any): FormData => {
	if (data instanceof FormData) return data;
	const token = encodeToken(data);
	const tokenRole = encodeToken({
		userId: data.userId,
		isAdmin: data.isAdmin,
	});

	const formData = new FormData();
	if (data.logoUrl) formData.append('file', data.logoUrl);
	formData.append('token', token);
	formData.append('tokenRole', tokenRole);

	const fields = [
		'name',
		'cifDni',
		'phoneNumber',
		'email',
		'address',
		'postalCode',
		'countryId',
		'provinceCountryTableId',
		'cityProvinceTableId',
	];
	fields.forEach(field => {
		if (data[field] !== undefined && data[field] !== null) {
			formData.append(field, data[field]);
		}
	});
	return formData;
};

const buildUpdateCenterFormData = (data: any): FormData => {
	if (data instanceof FormData) return data;
	const token = encodeToken(data);
	const formData = new FormData();
	if (data.logoUrl) formData.append('file', data.logoUrl);
	formData.append('token', token);
	return formData;
};

export const centerApi = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getAllCenter: builder.query<any[], void>({
			query: () => `/api/v1/centers`,
			transformResponse: (response: any) => {
				const token = response?.token ?? response?.data?.token ?? response?.data?.data?.token;
				if (token) {
					const centers = decodeToken(token);
					return Array.isArray(centers) ? centers : [];
				}
				const data = response?.data?.data ?? response?.data ?? response?.message;
				if (Array.isArray(data)) return data;
				if (Array.isArray(response)) return response;
				return [];
			},
			providesTags: ['Center'],
		}),
		getFullCenter: builder.query({
			query: centerId => `/api/v1/centers/${centerId}`,
			transformResponse: (response: any) => {
				const token = response?.token ?? response?.data?.token ?? response?.data?.data?.token;
				if (token) return decodeToken(token);
				return response?.data?.data ?? response?.data ?? response;
			},
			providesTags: (result, error, id) => [{ type: 'Center', id }],
		}),
		createCenter: builder.mutation({
			query: data => ({
				url: `/api/v1/centers`,
				method: 'POST',
				body: buildCreateCenterFormData(data),
			}),
			invalidatesTags: ['Center'],
		}),
		updateCenter: builder.mutation({
			query: data => {
				const centerId = data instanceof FormData ? data.get('centerId') : data.centerId;
				return {
					url: `/api/v1/centers/${centerId}`,
					method: 'PATCH',
					body: buildUpdateCenterFormData(data),
				};
			},
			invalidatesTags: (result, error, arg) => {
				const id = arg instanceof FormData ? arg.get('centerId') : arg?.centerId;
				return [{ type: 'Center', id }, 'Center'];
			},
		}),
		deletedCenter: builder.mutation({
			query: centerId => ({
				url: `/api/v1/centers/${centerId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Center'],
		}),
		restoreCenter: builder.mutation({
			query: ({ centerId, data }) => ({
				url: `/api/v1/center/restore/${centerId}`,
				method: 'PATCH',
				body: data,
			}),
			invalidatesTags: ['Center'],
		}),
	}),
});

export const {
	useGetAllCenterQuery,
	useGetAllCenterQuery: useGetAllCentersQuery,
	useGetFullCenterQuery,
	useCreateCenterMutation,
	useUpdateCenterMutation,
	useDeletedCenterMutation,
	useRestoreCenterMutation,
} = centerApi;
